.controls {
  display: flex;
  flex-wrap: wrap;
  align-items: end;
  justify-content: space-between;
  gap: var(--space-m);
}

.pair {
  display: flex;
  flex-wrap: wrap;
  align-items: end;
  gap: var(--space-s);
}

/*
 * The colour is the only thing tying a dropdown to a shape on the wheel, so it
 * sits on the control itself rather than on a legend the reader has to find.
 */
.pick {
  border-left: 3px solid transparent;
  padding-left: var(--space-xs);
}

.pickA {
  border-left-color: var(--compare-a);
}

.pickB {
  border-left-color: var(--compare-b);
}

.filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-xs);
}

.levels {
  display: flex;

  padding: var(--space-3xs);

  background-color: var(--field);

  border: 1px solid var(--border);
  border-radius: var(--radius-pill);
}

.level {
  padding: var(--space-2xs) var(--space-s);

  font-family: inherit;
  font-size: var(--size-200);
  font-weight: var(--weight-medium);

  color: var(--text-muted);

  background: none;
  border: none;
  border-radius: var(--radius-pill);

  cursor: pointer;
  transition: color var(--duration-fast) var(--ease),
    background-color var(--duration-fast) var(--ease);
}

.levelActive {
  color: var(--on-accent);

  background-color: var(--accent);
}

.reset {
  padding: 0;

  font-size: var(--size-200);

  color: var(--accent);

  background: none;
  border: none;

  cursor: pointer;
}

.reset:hover {
  color: var(--accent-strong);

  text-decoration: underline;
}

/*
 * Whose weighting the page is read through. One line under the controls, so the
 * reader knows before the wheel that the numbers in the legend are theirs.
 */
.lensRow {
  display: flex;
  flex-wrap: wrap;
  align-items: end;
  gap: var(--space-s) var(--space-l);
}

.lensPick {
  min-width: 16rem;
}

.lensNote {
  display: flex;
  align-items: center;
  gap: var(--space-xs);

  font-size: var(--size-200);

  color: var(--text-muted);
}

.lensNote strong {
  color: var(--text);
}

.lensSwatch {
  display: inline-block;

  width: 0.5rem;
  height: 0.5rem;

  background: linear-gradient(
    90deg,
    var(--relevancy-less),
    var(--relevancy-equal),
    var(--relevancy-much-more)
  );

  border-radius: var(--radius-pill);
}

/* The persona's total in the legend: label, direction, number, one breath. */
.weighted {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2xs);

  padding-inline-start: var(--space-xs);

  border-inline-start: 1px solid var(--border-strong);
}

.weightedFor {
  color: var(--text-muted);
}

.weightedValue {
  font-weight: var(--weight-strong);
  font-variant-numeric: tabular-nums;

  color: var(--text);
}

.shiftIcon {
  width: 0.625rem;
  height: 0.625rem;

  fill: var(--shift, var(--text-faint));
}

.shiftup {
  --shift: var(--band-solid);
}

.shiftdown {
  --shift: var(--band-missing);
}

.shiftlevel {
  --shift: var(--text-faint);
}

.note {
  padding: var(--space-l) 0;

  font-size: var(--size-200);
  text-align: center;

  color: var(--text-muted);
}

/*
 * The ranked read of the same axes the wheel draws. The wheel shows the shape
 * of two products; this says, in order, what actually separates them. It stands
 * apart from the chart on a hairline rather than in a box, because it is the
 * same conversation continued and not a second section.
 */
.differences {
  display: flex;
  flex-direction: column;
  gap: var(--space-m);

  padding-top: var(--space-l);

  border-top: 1px solid var(--border);
}

.differencesHead {
  display: flex;
  flex-direction: column;
  gap: var(--space-2xs);
}

.differencesTitle {
  font-size: var(--size-400);
  font-weight: var(--weight-strong);
  line-height: var(--leading-tight);
}

.differencesIntro {
  max-width: var(--measure-text);

  font-size: var(--size-200);

  color: var(--text-muted);
}

/* The key for the thin bar: a plain bar over a thin bright one, the way a row draws them. */
.keySwatch {
  display: inline-block;

  width: 1.5rem;
  height: 0.625rem;

  margin-inline-end: var(--space-xs);

  vertical-align: middle;

  background:
    linear-gradient(var(--compare-b), var(--compare-b)) 0 0 / 100% 0.25rem no-repeat,
    linear-gradient(var(--compare-b), var(--compare-b)) 0 100% / 60% 0.1875rem no-repeat;

  opacity: 0.85;
}

.table {
  width: 100%;

  border-collapse: collapse;
}

/*
 * The header carries two product names, so it stays in their own casing rather
 * than in a small-caps label style: a name set in capitals runs wide, and these
 * columns have no width to spare.
 *
 * Every header rule is written at the same weight as the alignment it has to
 * beat, because a bare `.scoreHead` loses to `.table thead th` and the labels
 * then sit over the wrong columns.
 */
.table thead th {
  padding: 0 0 var(--space-xs);

  font-size: var(--size-200);
  font-weight: var(--weight-medium);
  text-align: start;

  color: var(--text-muted);

  border-bottom: 1px solid var(--border);
}

.table thead th.axisHead {
  width: 100%;
}

/* Each header takes its body cell's own padding, so the two share an edge. */
.table thead th.scoreHead {
  padding-inline: var(--space-s) 1.5rem;

  white-space: nowrap;

  text-align: end;
}

/* The gap column is held to one width so the bars share a baseline down the page. */
.table thead th.gapHead {
  width: 18rem;
  padding-inline-start: var(--space-l);

  text-align: end;
}

.swatch {
  display: inline-block;

  width: 0.5rem;
  height: 0.5rem;

  margin-inline-end: var(--space-2xs);

  vertical-align: middle;

  border-radius: var(--radius-pill);
}

.swatchA {
  background-color: var(--compare-a);
}

.swatchB {
  background-color: var(--compare-b);

  border-radius: var(--radius-xs);
}

/*
 * An entry is a row group: the difference itself, and the parts it is made of
 * once the reader folds it open. The rule sits under the group rather than
 * under the row, so an open entry reads as one block instead of five loose
 * lines.
 */
.group:not(:last-child) {
  border-bottom: 1px solid var(--border);
}

.row td,
.row th {
  padding: var(--space-xs) 0;

  vertical-align: middle;
}

.axis {
  padding-inline-end: var(--space-m);

  font-weight: var(--weight-regular);
  text-align: start;
}

/* The whole label is the handle, the way a panel heading is on every other screen. */
.disclosure {
  display: flex;
  align-items: center;
  gap: var(--space-2xs);

  padding: 0;

  font: inherit;
  color: inherit;
  text-align: start;

  background-color: transparent;

  border: none;

  cursor: pointer;
}

.disclosure:hover {
  color: var(--accent);
}

.twisty {
  display: flex;
  flex: none;

  color: var(--text-faint);
}

.disclosure:hover .twisty {
  color: var(--accent);
}

.axisLabel {
  display: block;

  font-size: var(--size-300);
  font-weight: var(--weight-regular);
  line-height: var(--leading-tight);

  color: var(--text);
}

/* Which topic a capability belongs to; a capability label alone is ambiguous. */
.axisGroup {
  display: block;

  margin-top: var(--space-3xs);

  font-size: var(--size-100);

  color: var(--text-muted);
}

.score {

  font-size: var(--size-300);
  font-variant-numeric: tabular-nums;
  text-align: end;
  white-space: nowrap;

  color: var(--text-muted);
}

/* The heavier of the two numbers is the one ahead; no extra column says so. */
.leading {
  font-weight: var(--weight-strong);

  color: var(--text);
}

/*
 * The number and, where the author explained it, the mark that opens the
 * reasoning. The mark hangs off the number's right edge in the gutter the cell
 * already has, so a column of marked and unmarked numbers still lines up on
 * the digits.
 */
.scoreInner {
  position: relative;

  display: inline-block;
}

.mark {
  position: absolute;
  top: 50%;
  left: 100%;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  width: 1.25rem;
  height: 1.25rem;
  margin-inline-start: var(--space-2xs);

  font-size: var(--size-300);

  color: var(--text-muted);
  background: none;
  border: none;
  border-radius: var(--radius-xs);

  transform: translateY(-50%);

  cursor: pointer;
  transition: color var(--duration-fast) var(--ease),
    background-color var(--duration-fast) var(--ease);
}

.mark:hover {
  color: var(--accent-strong);
  background-color: var(--accent-soft);
}

.mark:focus-visible {
  color: var(--accent-strong);

  outline: none;
  box-shadow: var(--ring);
}


.gapCell {
  padding-inline-start: var(--space-l);
}

/* The cell itself stays a table cell, so every column keeps its edge. */
.gapInner {
  display: flex;
  align-items: center;
  gap: var(--space-s);
}

/*
 * A diverging bar: the centre line is parity, and the fill leaves it towards
 * whichever product is ahead. Sorted by width, the column reads as a staircase
 * before a single figure has been read.
 */
.meter {
  position: relative;

  flex: 1;
  min-width: 8rem;
  height: 0.5rem;
}

.meter::before {
  content: "";

  position: absolute;
  inset-block: -0.125rem;
  left: calc(50% - 0.5px);

  width: 1px;

  background-color: var(--text-faint);
}

.fill {
  position: absolute;
  inset-block: 0;

  border-radius: var(--radius-pill);

  transition: width var(--duration-normal) var(--ease);
}

/*
 * The distance after the persona's weighting, under the plain one: same side,
 * thinner and brighter, so a gap the persona rated away is seen shrinking under
 * a bar that stays where the data put it.
 */
.fillWeighted {
  inset-block: auto 0;

  height: 0.1875rem;

  filter: brightness(1.35);
}

.meter:has(.fillWeighted) .fill:not(.fillWeighted) {
  inset-block: 0 0.25rem;

  opacity: 0.55;
}

.meter:has(.fillWeighted) {
  height: 0.625rem;
}

.fillA {
  right: 50%;

  background-color: var(--compare-a);
}

.fillB {
  left: 50%;

  background-color: var(--compare-b);
}

.gapValue {
  min-width: 2.5rem;

  font-size: var(--size-300);
  font-weight: var(--weight-medium);
  font-variant-numeric: tabular-nums;
  text-align: end;

  color: var(--text);
}

/*
 * What the list deliberately leaves out. Agreement and a one-sided score are
 * both facts about the pair, and dropping them silently would let the list
 * imply the catalogue is thinner than it is.
 */
.footnote {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-xs) var(--space-m);

  font-size: var(--size-100);

  color: var(--text-muted);
}

/* Heard, not seen: which side is ahead, for a reader who gets only the numbers. */
.spoken {
  position: absolute;

  width: 1px;
  height: 1px;
  overflow: hidden;

  clip-path: inset(50%);

  white-space: nowrap;
}

@media (width < 40rem) {
  /* The bar is the first thing to go; the figure beside it carries the fact. */
  .meter,
  .keyLine {
    display: none;
  }

  .gapCell {
    padding-inline-start: var(--space-s);
  }

  .table thead th.gapHead {
    width: auto;
    padding-inline-start: var(--space-s);
  }

  .differencesTitle {
    font-size: var(--size-300);
  }
}

/*
 * What an axis is made of. The parts stay in the parent table so every column
 * keeps its edge; only the label indents, which is what says they belong to the
 * row above rather than standing beside it.
 */
.partsHead th {
  padding: var(--space-s) 0 var(--space-2xs) var(--space-l);

  font-size: var(--size-100);
  font-weight: var(--weight-medium);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  text-align: start;

  color: var(--text-muted);
}

.partRow td,
.partRow th {
  padding: var(--space-2xs) 0;

  vertical-align: baseline;
}

.partRow:last-child td,
.partRow:last-child th {
  padding-bottom: var(--space-m);
}

.partAxis {
  padding-inline: var(--space-l) var(--space-m);

  font-weight: var(--weight-regular);
  text-align: start;
}

.partLabel {
  font-size: var(--size-200);

  color: var(--text-muted);
}

.partScore {
  font-size: var(--size-200);
}

/* A side with no score; it is a gap in the data, never a zero. */
.absent {
  font-size: var(--size-100);

  color: var(--text-muted);
}

.partMeter {
  height: 0.375rem;
}

.partGap {
  font-size: var(--size-200);
  font-weight: var(--weight-regular);

  color: var(--text-muted);
}

.partGapCell {
  font-size: var(--size-200);
}

/* Why a part has no bar: the two are level, or only one of them was scored. */
.partNote {
  display: block;

  font-size: var(--size-100);
  text-align: end;

  color: var(--text-muted);
}

.partEmpty {
  padding-bottom: var(--space-m);
  padding-inline-start: var(--space-l);

  font-size: var(--size-200);

  color: var(--text-muted);
}

/*
 * The inline paddings of the body cells, written at the weight of the row
 * rules above them (`.row td`, `.partRow td`), which otherwise zero them and
 * leave the header over a different edge than the numbers. The right gutter of
 * a score cell is where a reasoning mark hangs, so it is wider than the left.
 */
td.score {
  padding-inline: var(--space-s) 1.5rem;
}

td.gapCell {
  padding-inline-start: var(--space-l);
}

/* Narrow screens have no width to spare; the mark's gutter shrinks to what it needs. */
@media (width < 40rem) {
  td.score,
  .table thead th.scoreHead {
    padding-inline: 0 1.125rem;
  }

  td.gapCell {
    padding-inline-start: 0;
  }

  .mark {
    width: 1rem;
    height: 1rem;
    margin-inline-start: var(--space-3xs);
  }
}
